import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Users, ShieldCheck, LineChart, MessageCircle, Bookmark } from "lucide-react";
import Logo from "@/components/Logo";
import HamburgerMenu from "@/components/HamburgerMenu";
import HeroIntro from "@/components/HeroIntro";
import RecentlySold from "@/components/RecentlySold";
import BuildOfTheMonth from "@/components/BuildOfTheMonth";
import { useAuth } from "@/context/AuthContext";
import { BURNOUT_HERO, SRT_LOGO } from "@/lib/assets";
import Seo, { faqSchema, organizationSchema, websiteSchema } from "@/components/Seo";

const FAQS = [
    { q: "What is SRT Finder?", a: "SRT Finder is a dedicated online marketplace and community for high-performance Mopar vehicles built exclusively for the Dodge Charger SRT Hellcat, Challenger SRT Hellcat Redeye, Widebody, Scat Pack, SRT Demon and Demon 170, Chrysler 300 SRT8, and RAM performance trucks like the TRX and RHO. Only SRT and R/T trim vehicles are permitted." },
    { q: "Where can I buy a Dodge Hellcat Charger or Challenger?", a: "Buy directly from private sellers and vetted dealers on SRT Finder. Every listing includes verified specs, mileage, mods list, high-resolution photos, seller ratings, and a real market-average price computed from active comparable SRT and R/T listings." },
    { q: "What models can be listed on SRT Finder?", a: "Dodge Charger (SRT8, R/T Scat Pack, Widebody, Hellcat, Redeye), Dodge Challenger (R/T, Scat Pack, Widebody, SRT8, Hellcat, Redeye, Super Stock, SRT Demon, Demon 170), Chrysler 300 SRT8, and RAM 1500 TRX, RHO, and Limited performance trucks." },
    { q: "Is SRT Finder better than Facebook Marketplace or Craigslist for buying a Hellcat?", a: "Yes. Unlike general marketplaces, SRT Finder is 100% dedicated to SRT and R/T Mopar performance vehicles — no V6 tire-kickers, no unrelated listings, no scams disguised as base-trim Chargers. Listings are structured so buyers can filter by exact trim, year, mileage, mods and location." },
];

export default function Landing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    // Play the intro once per tab session, and every hard reload of the home screen.
    const [introDone, setIntroDone] = useState(() => {
        try {
            return sessionStorage.getItem("srtf_intro_played") === "1";
        } catch {
            return false;
        }
    });

    useEffect(() => {
        if (user) navigate("/marketplace", { replace: true });
    }, [user, navigate]);

    function finishIntro() {
        setIntroDone(true);
        try {
            sessionStorage.setItem("srtf_intro_played", "1");
        } catch {}
    }

    return (
        <div className="min-h-screen text-white overflow-x-hidden relative">
            {!introDone && <HeroIntro onDone={finishIntro} />}
            <Seo
                title="Buy, Sell &amp; Trade Dodge Hellcat, Challenger SRT, Charger SRT, TRX, Demon &amp; Scat Pack"
                description="SRT Finder is the #1 dedicated marketplace and community for high-performance Mopar vehicles. Buy, sell, and trade Dodge Charger SRT Hellcat, Challenger SRT Hellcat Redeye, Widebody Scat Pack, SRT Demon &amp; Demon 170, Chrysler 300 SRT8, and RAM TRX &amp; RHO trucks."
                canonicalPath="/"
                image={BURNOUT_HERO}
                imageAlt="Matte-black Dodge Charger SRT Hellcat Widebody — SRT Finder Mopar performance marketplace"
                jsonLd={[faqSchema(FAQS), organizationSchema(), websiteSchema()]}
            />

            {/* Page-wide seamless background — no hero/section seam */}
            <div
                className="fixed inset-0 pointer-events-none"
                aria-hidden="true"
                style={{
                    zIndex: 0,
                    background:
                        "radial-gradient(ellipse at 20% 15%, rgba(225,6,0,0.32) 0%, transparent 55%), radial-gradient(ellipse at 85% 85%, rgba(122,4,0,0.24) 0%, transparent 60%), linear-gradient(180deg, #050505 0%, #0a0505 40%, #050505 100%)",
                }}
            />

            {/* SVG filter defs — subtle glow filter for the SRT logo */}
            <svg
                width="0"
                height="0"
                style={{ position: "absolute" }}
                aria-hidden="true"
            >
                <defs>
                    <filter id="srt-glow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>
            </svg>

            {/* ============= HERO ============= */}
            <section className="relative">
                {/* Challenger — cartoon silhouette in burnout, sits BEHIND the hero copy */}
                <div
                    className="absolute inset-0 pointer-events-none flex items-center justify-center"
                    aria-hidden="true"
                    style={{ zIndex: 1 }}
                >
                    <img
                        src={BURNOUT_HERO}
                        alt=""
                        className="h-auto w-full max-w-[1400px] object-contain select-none"
                        loading="eager"
                        fetchPriority="high"
                        style={{
                            opacity: 0.85,
                            transform: "translateY(6%) scale(1.05)",
                            filter:
                                "drop-shadow(0 20px 40px rgba(0,0,0,0.85)) drop-shadow(0 0 120px rgba(225,6,0,0.22))",
                        }}
                    />
                </div>

                {/* Subtle red under-glow on the car */}
                <div
                    className="absolute pointer-events-none"
                    aria-hidden="true"
                    style={{
                        zIndex: 2,
                        left: "10%",
                        right: "10%",
                        top: "45%",
                        height: "40%",
                        background:
                            "radial-gradient(ellipse at 55% 55%, rgba(225,6,0,0.18) 0%, transparent 65%)",
                        filter: "blur(60px)",
                    }}
                />

                {/* Sticky nav — real SRT logo lives here (top of page) */}
                <header className="relative z-30">
                    <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
                        <Logo size="sm" />
                        <div className="flex items-center gap-2">
                            <Link
                                to="/marketplace"
                                data-testid="landing-nav-marketplace"
                                className="hidden sm:inline-flex text-sm font-semibold text-white hover:text-hellcat px-3 py-2"
                                style={{ transition: "color 0.15s" }}
                            >
                                Marketplace
                            </Link>
                            <Link
                                to="/community"
                                data-testid="landing-nav-community"
                                className="hidden sm:inline-flex text-sm font-semibold text-white hover:text-hellcat px-3 py-2"
                                style={{ transition: "color 0.15s" }}
                            >
                                Community
                            </Link>
                            <Link
                                to="/login"
                                data-testid="landing-login-link"
                                className="hidden sm:inline-flex btn-ghost px-4 py-2 text-sm font-bold"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/register"
                                data-testid="landing-register-link"
                                className="btn-red px-4 py-2 text-sm font-bold"
                            >
                                Join free
                            </Link>
                            <HamburgerMenu testid="landing-hamburger" />
                        </div>
                    </div>
                </header>

                {/* Hero copy — sits ON TOP of the charger */}
                <div className="relative z-20">
                    <div className="w-full max-w-6xl mx-auto px-5 pt-6 sm:pt-10 pb-16 sm:pb-24">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            className="text-center max-w-4xl mx-auto"
                        >
                            <div
                                className="chip inline-flex mx-auto"
                                style={{
                                    background: "rgba(225,6,0,0.14)",
                                    borderColor: "rgba(225,6,0,0.4)",
                                    color: "#FF6E68",
                                }}
                            >
                                <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ background: "#E10600" }}
                                />
                                Live · SRT &amp; R/T only · Zero V6s
                            </div>

                            {/* Full SRT Finder brand logo (splatter mark) */}
                            <h1
                                data-testid="hero-wordmark"
                                className="mt-8 sm:mt-12 flex justify-center"
                                aria-label="SRT Finder"
                            >
                                <img
                                    src={SRT_LOGO}
                                    alt="SRT Finder — the exclusive Mopar performance marketplace"
                                    className="w-full max-w-[720px] h-auto select-none"
                                    style={{
                                        filter: "drop-shadow(0 0 40px rgba(225,6,0,0.45)) drop-shadow(0 10px 30px rgba(0,0,0,0.85))",
                                    }}
                                    draggable={false}
                                />
                            </h1>

                            <p
                                className="mt-8 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed"
                                style={{
                                    color: "rgba(255,255,255,0.82)",
                                    textShadow: "0 2px 8px rgba(0,0,0,0.7)",
                                }}
                            >
                                Buy, sell, and trade SRT Mopars — with verified sellers, real
                                market pricing, and a community of performance owners.
                            </p>

                            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
                                <Link
                                    to="/marketplace"
                                    data-testid="landing-browse-btn"
                                    className="btn-red px-7 py-4 inline-flex items-center gap-2 text-sm font-bold"
                                >
                                    Browse SRT
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link
                                    to="/register"
                                    data-testid="landing-create-account-btn"
                                    className="btn-ghost px-7 py-4 text-sm font-bold"
                                >
                                    Create your Garage
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ============= VALUE PILLARS — Why SRT Finder ============= */}
            <BuildOfTheMonth />
            <RecentlySold limit={8} />

            <section className="relative z-10 max-w-6xl mx-auto px-5 pt-8 pb-20 sm:pt-14 sm:pb-28">
                <div className="max-w-2xl mx-auto text-center">
                    <div className="label-caps text-hellcat">Why SRT Finder</div>
                    <h2
                        className="mt-3 text-4xl sm:text-5xl text-white"
                        style={{
                            fontFamily: "'DM Serif Display', serif",
                            fontWeight: 400,
                            letterSpacing: "-0.005em",
                        }}
                    >
                        The exclusive marketplace for
                        <span style={{ color: "#E10600" }}> SRT &amp; R/T</span> Mopar performance.
                    </h2>
                </div>
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                        { icon: Zap, title: "SRT-only. No V6.", body: "Hellcat, Redeye, Demon, Super Stock, Scat Pack, TRX, RHO. Hand-curated for Mopar performance." },
                        { icon: LineChart, title: "Real market pricing", body: "Every listing priced against active comparable SRT and R/T sales — a fair number, always." },
                        { icon: ShieldCheck, title: "Verified sellers", body: "Buyer-rated sellers, admin-vetted dealers, public rating history on every profile." },
                        { icon: Users, title: "Community first", body: "Build threads, tech help, meets & burnouts. A message board just for Mopar performance owners." },
                        { icon: MessageCircle, title: "Direct messaging", body: "Message sellers, send trade proposals, share photos, close deals — inside the app." },
                        { icon: Bookmark, title: "Saved searches", body: "Get first pick on the Hellcat or TRX you've been hunting — save filters, come back later." },
                    ].map((f, i) => (
                        <motion.article
                            key={f.title}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ delay: i * 0.04 }}
                            className="rounded-3xl p-6"
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(20,20,20,0.6) 0%, rgba(20,20,20,0.3) 100%)",
                                border: "1px solid rgba(255,255,255,0.06)",
                                backdropFilter: "blur(14px)",
                            }}
                        >
                            <div
                                className="w-10 h-10 rounded-full flex items-center justify-center mb-4"
                                style={{
                                    background: "rgba(225,6,0,0.14)",
                                    border: "1px solid rgba(225,6,0,0.4)",
                                }}
                            >
                                <f.icon className="w-5 h-5 text-hellcat" strokeWidth={2} />
                            </div>
                            <h3
                                className="text-white text-lg"
                                style={{
                                    fontFamily: "'DM Serif Display', serif",
                                    fontWeight: 400,
                                }}
                            >
                                {f.title}
                            </h3>
                            <p className="text-sm text-white/90 mt-2 leading-relaxed">
                                {f.body}
                            </p>
                        </motion.article>
                    ))}
                </div>
            </section>

            {/* Footer */}
            <footer className="relative z-10 border-t border-white/5">
                <div className="max-w-6xl mx-auto px-5 py-8 flex flex-wrap items-center justify-between gap-4 text-xs text-onyx-500">
                    <div className="flex items-center gap-3">
                        <Logo size="xs" />
                        <span>© {new Date().getFullYear()} SRT Finder · Built for the Hellcat crowd</span>
                    </div>
                    <div className="flex items-center gap-4 label-caps">
                        <Link to="/terms" className="hover:text-white" data-testid="footer-terms">
                            Terms
                        </Link>
                        <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">
                            Privacy
                        </Link>
                        <a
                            href="mailto:support@srtfinder.com"
                            className="hover:text-white"
                            data-testid="footer-contact"
                        >
                            Contact
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
